import { ContextMenu as C } from "radix-ui";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { cn } from "../../lib/cn";

/*
 * Right-click (and long-press on touch) menus. Same look as Menu.tsx so a
 * context menu and the matching "⋯" dropdown are visually interchangeable.
 *
 *   <ContextMenu>
 *     <ContextMenuTrigger asChild><div>…</div></ContextMenuTrigger>
 *     <ContextMenuContent>
 *       <ContextMenuItem icon={IconReply} onSelect={…}>Reply</ContextMenuItem>
 *     </ContextMenuContent>
 *   </ContextMenu>
 */

export const ContextMenu = C.Root;
export const ContextMenuTrigger = C.Trigger;
export const ContextMenuSub = C.Sub;

const contentClass = "z-[80] min-w-52 max-w-72 rounded-lg bg-surface p-1 shadow-pop animate-pop-in focus:outline-none";

export function ContextMenuContent({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <C.Portal>
      <C.Content collisionPadding={8} className={cn(contentClass, className)} onClick={(e) => e.stopPropagation()}>
        {children}
      </C.Content>
    </C.Portal>
  );
}

const itemClass =
  "flex h-9 cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40";

export function ContextMenuItem({
  icon: Icon,
  children,
  onSelect,
  danger,
  disabled,
  hint,
}: {
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  return (
    <C.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(itemClass, danger ? "text-danger data-[highlighted]:bg-danger-soft" : "text-fg data-[highlighted]:bg-surface-2")}
    >
      {Icon && <Icon className={cn("icon size-[18px]", danger ? "text-danger" : "text-fg-2")} aria-hidden />}
      <span className="flex-1 truncate">{children}</span>
      {hint && <span className="text-xs text-fg-3">{hint}</span>}
    </C.Item>
  );
}

export function ContextMenuCheckboxItem({
  icon: Icon,
  children,
  checked,
  onCheckedChange,
  disabled,
}: {
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <C.CheckboxItem checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} onSelect={(e) => e.preventDefault()} className={cn(itemClass, "text-fg data-[highlighted]:bg-surface-2")}>
      {Icon && <Icon className="icon size-[18px] text-fg-2" aria-hidden />}
      <span className="flex-1 truncate">{children}</span>
      <span className={cn("size-4 rounded border border-line-strong", checked && "border-accent bg-accent")} aria-hidden />
    </C.CheckboxItem>
  );
}

export function ContextMenuSubTrigger({ icon: Icon, children }: { icon?: ComponentType<SVGProps<SVGSVGElement>>; children: ReactNode }) {
  return (
    <C.SubTrigger className={cn(itemClass, "text-fg data-[highlighted]:bg-surface-2 data-[state=open]:bg-surface-2")}>
      {Icon && <Icon className="icon size-[18px] text-fg-2" aria-hidden />}
      <span className="flex-1 truncate">{children}</span>
      <span className="text-fg-3" aria-hidden>
        ›
      </span>
    </C.SubTrigger>
  );
}

export function ContextMenuSubContent({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <C.Portal>
      <C.SubContent collisionPadding={8} sideOffset={4} className={cn(contentClass, className)}>
        {children}
      </C.SubContent>
    </C.Portal>
  );
}

export function ContextMenuSeparator() {
  return <C.Separator className="-mx-1 my-1 h-px bg-line" />;
}

export function ContextMenuLabel({ children }: { children: ReactNode }) {
  return <C.Label className="px-2.5 pb-1 pt-1.5 text-xs font-medium text-fg-3">{children}</C.Label>;
}

/** A row of custom content (e.g. quick reactions) that doesn't behave like a menu item. */
export function ContextMenuGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <C.Group className={className}>{children}</C.Group>;
}
